import type { CoverageKind, CoverageStatusCode, CoverageHistoryEntry, CoverageRecord } from '../../api/client';

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
/** Drawer section counts (shown beside the section title whether it is open or closed). Records and files are counted separately; voided records are not "records" but are mentioned. */
export function coverageSummary(list: CoverageRecord[]): string {
  const live = list.filter((c) => c.state !== 'void');
  const files = new Set<string>();
  for (const c of live) for (const f of c.files || []) files.add(f.fileId);
  const voided = list.length - live.length;
  const head = live.length ? `${plural(live.length, 'record')} · ${plural(files.size, 'file')}` : 'No records';
  return voided ? `${head} · ${voided} voided` : head;
}
export function contactsSummary(contacts: Array<{ isActive: boolean }>): string {
  const active = contacts.filter((c) => c.isActive).length; const inactive = contacts.length - active;
  const head = active ? plural(active, 'contact') : 'No contacts';
  return inactive ? `${head} · ${inactive} inactive` : head;
}
/** Notes is ONE text field on the vendor, so the honest count is whether a note exists. */
export const notesSummary = (notes: string | null | undefined): string => (notes && notes.trim() ? 'Note added' : 'No note');

/** BUILD-043 Documents & compliance - small pure helpers for the screens. Dates are "YYYY-MM-DD" strings; they are shown and typed as MM/DD/YYYY by string math only (no time-zone shifts). */
export const COVERAGE_KINDS: Array<{ value: CoverageKind; label: string }> = [
  { value: 'general_liability', label: 'General liability insurance' }, { value: 'auto_liability', label: 'Auto liability insurance' }, { value: 'workers_comp', label: "Workers' compensation insurance" },
  { value: 'contractor_license', label: 'Contractor license' }, { value: 'professional_liability', label: 'Professional liability insurance' }, { value: 'umbrella_excess', label: 'Umbrella / excess insurance' },
  { value: 'agreement', label: 'Agreement' }, { value: 'other_license', label: 'Other license or certificate' },
];
export const MAX_FILE_BYTES = 4 * 1024 * 1024;
export const ALLOWED_EXT = ['pdf', 'jpg', 'jpeg', 'png'];

const isLeap = (y: number) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
const dim = (y: number, m: number) => [31, isLeap(y) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1];
/** "MM/DD/YYYY" -> "YYYY-MM-DD", or null when it is not a real date. */
export function parseUs(s: string): string | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(s || '').trim());
  if (!m) return null;
  const mo = Number(m[1]); const d = Number(m[2]); const y = Number(m[3]);
  if (y < 1900 || y > 2200 || mo < 1 || mo > 12 || d < 1 || d > dim(y, mo)) return null;
  return `${m[3]}-${m[1]}-${m[2]}`;
}
export function formatUs(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  return m ? `${m[2]}/${m[3]}/${m[1]}` : '';
}
/** One problem sentence for a typed date ('' = fine; empty is fine unless required). */
export function dateProblem(text: string, required = false): string {
  const t = text.trim();
  if (!t) return required ? 'Enter a date as MM/DD/YYYY.' : '';
  return parseUs(t) ? '' : 'Use a real date as MM/DD/YYYY, like 03/01/2027.';
}

// ── calendar helpers (BUILD-043 usability): pure date arithmetic on "YYYY-MM-DD" strings through UTC fields, so no local time-zone shift can move a day ──
export const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const MIN_YEAR = 1900; export const MAX_YEAR = 2200;
/** The Phoenix calendar date for an instant (Arizona is UTC-7 all year, no daylight saving). */
export const phoenixTodayIso = (nowMs: number = Date.now()): string => new Date(nowMs - 7 * 3600 * 1000).toISOString().slice(0, 10);
const parts = (iso: string) => { const [y, m, d] = iso.split('-').map(Number); return { y, m, d }; };
const mk = (y: number, m: number, d: number) => `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
export const daysInMonth = (y: number, m: number) => dim(y, m);
export function isoAddDays(iso: string, n: number): string { const { y, m, d } = parts(iso); return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10); }
export function isoWeekday(iso: string): number { const { y, m, d } = parts(iso); return new Date(Date.UTC(y, m - 1, d)).getUTCDay(); }
/** Move by whole months, keeping the day when the month has it and otherwise using the month's last day. */
export function isoAddMonths(iso: string, k: number): string {
  const { y, m, d } = parts(iso); const idx = y * 12 + (m - 1) + k; const ny = Math.floor(idx / 12); const nm = (idx % 12) + 1;
  return mk(ny, nm, Math.min(d, dim(ny, nm)));
}
export const inCalendarRange = (iso: string) => { const y = parts(iso).y; return y >= MIN_YEAR && y <= MAX_YEAR; };
export const firstOfMonth = (iso: string) => { const { y, m } = parts(iso); return mk(y, m, 1); };

export const STATUS_CHIP: Record<CoverageStatusCode, string> = {
  current: 'bg-green-50 text-green-800 border-green-200', expiring: 'bg-amber-50 text-amber-900 border-amber-300', expired: 'bg-red-50 text-red-800 border-red-300',
  not_yet: 'bg-blue-50 text-blue-800 border-blue-200', unknown: 'bg-orange-50 text-orange-900 border-orange-300', none: 'bg-gray-100 text-gray-700 border-gray-300',
  renewed: 'bg-gray-100 text-gray-600 border-gray-300', void: 'bg-gray-100 text-gray-500 border-gray-300 line-through',
};

export const ACTION_TEXT: Record<string, string> = {
  created: 'Added', renewal_created: 'Renewal recorded', updated: 'Edited', voided: 'Voided', file_attached: 'Document added', file_unlinked: 'Document removed from this record',
};
const FIELD_TEXT: Record<string, string> = { label: 'Name', issuer: 'Issuer', reference: 'Policy / license number', effectiveOn: 'Effective date', expiryMode: 'Expiration', expiresOn: 'Expiration date', notes: 'Notes', responsibleEmployeeId: 'Responsible person' };
/** Plain "what changed" lines for one history entry. */
export function changeText(h: CoverageHistoryEntry): string[] {
  const b = h.before; const a = h.after;
  if (!a) return [];
  if (h.action === 'file_attached' || h.action === 'file_unlinked') {
    const bf = new Set<string>((b?.files || []).map((f: any) => f.fileId)); const af = new Set<string>((a.files || []).map((f: any) => f.fileId));
    return [...(a.files || [])].filter((f: any) => !bf.has(f.fileId)).map((f: any) => `Added ${f.displayName}`).concat([...(b?.files || [])].filter((f: any) => !af.has(f.fileId)).map((f: any) => `Removed ${f.displayName}`));
  }
  if (h.action === 'voided') return [`Reason: ${a.voidReason}`];
  if (!b || h.action === 'created' || h.action === 'renewal_created') return [];
  const out: string[] = [];
  for (const k of Object.keys(FIELD_TEXT)) {
    if ((b[k] ?? null) !== (a[k] ?? null)) {
      const fmt = (v: any) => (v === null || v === undefined || v === '' ? 'blank' : /^\d{4}-\d{2}-\d{2}$/.test(String(v)) ? formatUs(String(v)) : k === 'expiryMode' ? ({ date: 'a date', none: 'none', unknown: 'not known' } as any)[v] : String(v));
      out.push(`${FIELD_TEXT[k]}: ${fmt(b[k])} → ${fmt(a[k])}`);
    }
  }
  return out;
}

/** Reads a File as base64 (no data: prefix) without building one huge intermediate string array. */
export async function fileToBase64(file: File): Promise<string> {
  const buf = new Uint8Array(await file.arrayBuffer());
  let bin = '';
  const CH = 0x8000;
  for (let i = 0; i < buf.length; i += CH) bin += String.fromCharCode.apply(null, Array.from(buf.subarray(i, i + CH)));
  return btoa(bin);
}
/** Client-side pre-check of a chosen file (the server checks again and is the authority). '' = fine. */
export function fileProblem(file: File | null): string {
  if (!file) return '';
  const ext = (/\.([A-Za-z0-9]{1,5})$/.exec(file.name) || [])[1]?.toLowerCase() || '';
  if (!ALLOWED_EXT.includes(ext)) return 'Only PDF, JPG and PNG files can be attached.';
  if (file.size === 0) return 'That file is empty.';
  if (file.size > MAX_FILE_BYTES) return 'That file is too large (the limit is 4 MB).';
  return '';
}
export function base64ToBlob(b64: string, type: string): Blob {
  const bin = atob(b64); const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return new Blob([out], { type });
}
